<template>
  <div class="product-saleItems-section" v-if="visible">
    <h3 class="section-title">
      <i class="fas fa-clock" style="width: 18px; height: 18px;"></i>
      Historique des ventes récentes
    </h3>
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Chargement des ventes...</p>
    </div>
    <div v-else-if="saleItems.length === 0" class="empty">
      <p>Aucune vente récente.</p>
    </div>
    <div v-else class="product-saleItems-table-container">
      <table class="product-saleItems-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Client</th>
            <th>Quantité</th>
            <th>Prix unitaire</th>
            <th>Remise</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(saleItem, index) in saleItems" :key="index">
            <td>{{ formatDate(saleItem.sale.sale_date) }}</td>
            <td>{{ saleItem.sale.customer?.name || '-' }}</td>
            <td>{{ saleItem.quantity }}</td>
            <td>{{ formatPrice(saleItem.unit_price) }}</td>
            <td>{{ formatPrice(saleItem.discount) }}</td>
            <td>{{ formatPrice(saleItem.total_amount) }}</td>
          </tr>
        </tbody>
      </table>
      <PaginationComponent v-if="pagination.totalPages > 1" :current-page="pagination.currentPage"
        :per-page="pagination.perPage" :total="pagination.total"
        :to="Math.min(pagination.currentPage * pagination.perPage, pagination.total)" @page-change="goToPage" />
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue'
import { productsService } from '@/services'
import PaginationComponent from '@/components/common/PaginationComponent.vue'

export default {
  name: 'ProductSalesHistory',
  components: {
    PaginationComponent
  },
  props: {
    productId: {
      type: [Number, String],
      default: null
    },
    // legacy prop (non utilisé avec pagination serveur)
    limit: {
      type: Number,
      default: 10
    },
    perPage: {
      type: Number,
      default: 10
    }
  },
  setup(props) {
    const saleItems = ref([])
    const loading = ref(false)
    const visible = computed(() => !!props.productId)
    const pagination = ref({
      currentPage: 1,
      totalPages: 1,
      total: 0,
      perPage: 10
    })

    const loadSales = async (page = 1) => {
      if (!props.productId) {
        saleItems.value = []
        return
      }
      loading.value = true
      try {
        const response = await productsService.getProductSales(props.productId, { per_page: pagination.value.perPage, page })
        console.log(response.data)
        // Gérer format réponse paginée de l'API
        if (response && response.data) {
          // La structure du backend est response.data.data (tableau des ventes), avec response.data.meta pour la pagination
          saleItems.value = response.data.data || []
          if (response.data.meta) {
            pagination.value = {
              currentPage: response.data.meta.current_page || 1,
              totalPages: response.data.meta.last_page || 1,
              total: response.data.meta.total || saleItems.value.length,
              perPage: response.data.meta.per_page || pagination.value.perPage
            }
          } else {
            // fallback si pas de meta
            pagination.value = {
              currentPage: 1,
              totalPages: 1,
              total: saleItems.value.length,
              perPage: saleItems.value.length
            }
          }
        } else {
          saleItems.value = []
          pagination.value = {
            currentPage: 1,
            totalPages: 1,
            total: 0,
            perPage: pagination.value.perPage
          }
        }
      } catch (e) {
        saleItems.value = []
      } finally {
        loading.value = false
      }
    }

    const goToPage = (page) => loadSales(page)
    const reload = () => loadSales(pagination.value.currentPage)

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: '2-digit' })
    }
    const formatPrice = (amount) => new Intl.NumberFormat('fr-FR', {
      style: 'currency', currency: 'XOF', minimumFractionDigits: 0
    }).format(amount || 0)

    watch(() => props.productId, () => {
      pagination.value.currentPage = 1
      loadSales(1)
    })
    onMounted(() => {
      // Toujours 10 par page côté serveur
      pagination.value.perPage = 10
      loadSales(1)
    })

    return { saleItems, loading, visible, pagination, goToPage, reload, formatDate, formatPrice }
  }
}
</script>

<style scoped>
.product-saleItems-table-container {
  overflow-x: auto;
  margin-top: 16px;
}

.product-saleItems-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.product-saleItems-table th,
.product-saleItems-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.product-saleItems-table th {
  background-color: #f8fafc;
  font-weight: 600;
  color: #334155;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-saleItems-table tr:hover td {
  background-color: #f8fafc;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 12px 0;
  padding: 16px;
}

.loading,
.empty {
  padding: 16px;
  color: #64748b;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #0A5C6B;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0)
  }

  100% {
    transform: rotate(360deg)
  }
}
</style>
